/**
 * 환경변수 접근 지점.
 *
 * import.meta.env 를 코드 곳곳에서 직접 읽지 않고 여기 한 곳으로 모은다.
 * - 오타가 런타임 undefined 로 새어나가지 않는다.
 * - 필수값 누락을 앱 시작 시점에 바로 알 수 있다.
 *
 * ★ 반드시 `import.meta.env.VITE_XXX` 처럼 **정적 프로퍼티**로 읽는다.
 *   `import.meta.env[name]` 같은 동적 접근을 쓰면 Vite 가 정적 치환을 못 해서
 *   env 객체 **전체**를 번들에 넣는다. 그러면 코드가 참조하지도 않는
 *   VITE_*_CLIENT_SECRET 까지 배포된 JS 에 그대로 실린다.
 *
 * ★ VITE_ 값은 참조하는 순간 번들에 노출된다. client_secret 은 이 파일에 없어야 한다.
 *   OAuth code -> token 교환은 백엔드(VITE_*_API)에서 처리한다.
 */

function required(name: string, value: string | undefined): string {
  if (!value) {
    throw new Error(`[env] ${name} 가 비어 있습니다. .env.${import.meta.env.MODE} 를 확인하세요.`);
  }
  return value;
}

function optional(name: string, value: string | undefined): string {
  if (!value && import.meta.env.DEV) {
    console.warn(`[env] ${name} 가 비어 있습니다. 관련 기능이 동작하지 않습니다.`);
  }
  return value ?? '';
}

export const env = {
  /** 백엔드 API 베이스 주소 */
  apiBase: required('VITE_API_BASE', import.meta.env.VITE_API_BASE),

  /** 세션스토리지 난독화 키 (번들 노출됨 — 기밀 저장 용도로 쓰지 말 것) */
  encryptKey: required('VITE_ENCRYPT', import.meta.env.VITE_ENCRYPT),

  /**
   * 업로드 파일 베이스 주소.
   *
   * 캠페인 이미지는 `/musicash/20260304/....jpg` 같은 **절대 경로**로 내려온다.
   * 구 프로젝트는 이 값을 그대로 `src` 에 넣었다 — 즉 프론트와 같은 오리진에서
   * 서빙된다는 뜻이다. 비워 두면 그 동작 그대로다. 별도 호스트로 옮기면 여기에
   * 넣는다.
   */
  uploadBase: import.meta.env.VITE_UPLOAD_BASE ?? '',

  /**
   * SNS 로 내보낼 주소의 호스트.
   *
   * 비우면 지금 보고 있는 오리진을 쓴다. 개발 중에 실제로 열리는 링크로 시험하고
   * 싶을 때만 채운다 — `dev.urnplay.com` 은 개발 PC 의 hosts 에만 있는 이름이라
   * 카카오톡으로 받은 사람이 눌러도 열리지 않는다.
   */
  shareBase: import.meta.env.VITE_SHARE_BASE ?? '',

  /**
   * 구글 분석 측정 ID. 둘 중 아무거나 넣으면 된다.
   *
   *   `G-XXXXXXXXXX`   GA4 측정 ID  (애널리틱스 > 관리 > 데이터 스트림 > 웹)
   *   `GTM-XXXXXXX`    태그 매니저  (태그 매니저 작업공간 우측 상단)
   *
   * 비우면 분석을 아예 붙이지 않는다. 개발 PC 의 조작이 실서버 통계에 섞이지 않게
   * `.env.development` 에서는 비워 둔다.
   *
   * ★ 이 값은 **비밀이 아니다.** 원래 브라우저에 드러나는 값이라 번들에 실려도 된다.
   */
  gaId: import.meta.env.VITE_GA_ID ?? '',

  /**
   * 안드로이드 앱이 플레이스토어에 올라가 있는가. `Y` 면 안드로이드 브라우저에
   * '앱에서 열기' 배너를 띄운다(`app-link.ts`).
   *
   * ★ 스토어에 올리기 **전에는 비워 둔다.** 앱이 없는 사람이 누르면 스토어로 가는데,
   *   올라가 있지 않으면 '찾을 수 없음' 화면이 뜬다.
   */
  androidApp: import.meta.env.VITE_ANDROID_APP === 'Y',

  google: {
    clientId: optional('VITE_GOOGLE_CLIENT_ID', import.meta.env.VITE_GOOGLE_CLIENT_ID),
    api: optional('VITE_GOOGLE_API', import.meta.env.VITE_GOOGLE_API),
  },

  naver: {
    clientId: optional('VITE_NAVER_CLIENT_ID', import.meta.env.VITE_NAVER_CLIENT_ID),
    callback: optional('VITE_NAVER_CALLBACK', import.meta.env.VITE_NAVER_CALLBACK),
    api: optional('VITE_NAVER_API', import.meta.env.VITE_NAVER_API),
  },

  kakao: {
    clientId: optional('VITE_KAKAO_CLIENT_ID', import.meta.env.VITE_KAKAO_CLIENT_ID),
    api: optional('VITE_KAKAO_API', import.meta.env.VITE_KAKAO_API),

    /**
     * 카카오톡 공유(JS SDK)용 **JavaScript 키**.
     *
     * 로그인에 쓰는 `clientId`(REST API 키)와 **다른 값**이다. 같은 앱이지만
     * 콘솔에서 따로 발급한다. 비어 있으면 카카오톡 공유는 브라우저 기본 공유로
     * 넘어간다 — 노출돼도 되는 값이라 프론트에 두는 것이 정상이다.
     */
    jsKey: optional('VITE_KAKAO_JS_KEY', import.meta.env.VITE_KAKAO_JS_KEY),
  },

  isDev: import.meta.env.DEV,
  isProd: import.meta.env.PROD,
} as const;
